import { useCallback, useMemo } from "react"

import { Formik, Form } from "formik"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { SearchIcon } from "@l2r-front/l2r-icons"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { ScrollableLayout, useIsMobileDevice } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { ANTITAGS_FILTER, FILTERS, STATUSES_FILTER, TAGS_FILTER, TECHNIQUES_FILTER, YEARS_FILTER } from "../../constants/filters"
import { statusesSettings, STATUSES } from "../../constants/status"
import { useRoadworksDispatchContext, useRoadworksStateContext } from "../../contexts/RoadworksContext/RoadworksContext.hooks"
import { roadworksFiltersConfig } from "../../utils/roadworksFiltersConfig"

import * as Styled from "./TechniquesFiltersForm.styled"

const defaultFormValues = {
    [YEARS_FILTER]: [],
    [TECHNIQUES_FILTER]: [],
    [STATUSES_FILTER]: [],
    [TAGS_FILTER]: [],
    [ANTITAGS_FILTER]: [],
}

export function TechniquesFiltersForm(props) {

    const { t } = useTranslation(I18N_NAMESPACE)
    const { project, filters } = useRoadworksStateContext()
    const { applyFilters } = useRoadworksDispatchContext()
    const isMobile = useIsMobileDevice()

    const {
        enabledFilters,
        className,
        close,
        onSubmit,
        years,
        techniques,
    } = props

    const initialTechniques = useMemo(() => {
        if (techniques) {
            return (filters.techniques || []).map(technique => {
                const acronym = techniques.find(t => t.code === technique)?.acronym
                return { code: technique, acronym: acronym }
            })
        } else {
            return []
        }
    }, [filters, techniques])

    const initialValues = useMemo(() => {
        return ({
            years: filters?.years || [],
            statuses: filters?.statuses || [],
            techniques: initialTechniques || [],
            tags: filters?.tags || [],
            antiTags: filters?.antiTags || [],
        })

    }, [filters, initialTechniques])

    const getFormFields = useCallback((setFieldValue,values) => {
        return <Styled.InputsWrapper>
            {
                enabledFilters.includes(YEARS_FILTER) && years && <Styled.AutoCompleteFilter
                    defaultValue={initialValues.years}
                    disableClearable
                    getOptionLabel={option => option.toString()}
                    id="year-filters-select"
                    label={t(I18N_NAMESPACE, roadworksFiltersConfig[YEARS_FILTER].label)}
                    limitTags={2}
                    name={YEARS_FILTER}
                    options={years}
                    popupIcon={<SearchIcon />}
                    setFieldValue={setFieldValue} />
            }
            {
                enabledFilters.includes(STATUSES_FILTER) && <Styled.AutoCompleteFilter
                    defaultValue={initialValues.statuses}
                    disableClearable
                    getOptionLabel={value => t(I18N_NAMESPACE, statusesSettings[value].label)}
                    id="status-filters-select"
                    label={t(I18N_NAMESPACE, roadworksFiltersConfig[STATUSES_FILTER].label)}
                    limitTags={2}
                    name={STATUSES_FILTER}
                    options={STATUSES}
                    popupIcon={<SearchIcon />}
                    setFieldValue={setFieldValue} />
            }
            {
                enabledFilters.includes(TECHNIQUES_FILTER) && techniques && <Styled.AutoCompleteFilter
                    defaultValue={initialValues.techniques}
                    disableClearable
                    getOptionLabel={option => option.acronym}
                    id="technique-filters-select"
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    label={t(I18N_NAMESPACE, roadworksFiltersConfig[TECHNIQUES_FILTER].label)}
                    limitTags={2}
                    name={TECHNIQUES_FILTER}
                    options={techniques}
                    popupIcon={<SearchIcon />}
                    setFieldValue={setFieldValue} />
            }
            {
                enabledFilters.includes(TAGS_FILTER) && project?.tags && <Styled.AutoCompleteFilter
                    defaultValue={initialValues.tags}
                    disableClearable
                    getOptionLabel={option => option}
                    id="tags-filters-select"
                    isOptionEqualToValue={(option, value) => option === value}
                    label={t(I18N_NAMESPACE, roadworksFiltersConfig[TAGS_FILTER].label)}
                    limitTags={2}
                    name={TAGS_FILTER}
                    options={project?.tags.filter(tag => !values.antiTags?.includes(tag))}
                    popupIcon={<SearchIcon />}
                    setFieldValue={setFieldValue} />
            }
            {
                enabledFilters.includes(ANTITAGS_FILTER) && project?.tags &&<Styled.AutoCompleteFilter
                    color="action/darkRed"
                    id="antiTags-filters-select"
                    disableClearable
                    filterSelectedOptions
                    label={t(I18N_NAMESPACE, roadworksFiltersConfig[ANTITAGS_FILTER].label)}
                    limitTags={2}
                    name="antiTags"
                    options={project?.tags.filter(tag => !values.tags?.includes(tag))}
                    setFieldValue={setFieldValue}
                    defaultValue={initialValues?.antiTags}
                    popupIcon={<SearchIcon />}
                />
            }
        </Styled.InputsWrapper >
    }, [enabledFilters, initialValues, project, t, techniques, years])

    const getFormButtons = useCallback((resetForm) => {
        return <>
            <Styled.Button
                id="reset-filters-button"
                color="error"
                onClick={() => {
                    const resettedFilters = {
                        ...filters,
                        ...(enabledFilters.includes(YEARS_FILTER) && { years: defaultFormValues.years }),
                        ...(enabledFilters.includes(STATUSES_FILTER) && { statuses: defaultFormValues.statuses }),
                        ...(enabledFilters.includes(TECHNIQUES_FILTER) && { techniques: defaultFormValues.techniques }),
                        ...(enabledFilters.includes(TAGS_FILTER) && { tags: defaultFormValues.tags }),
                        ...(enabledFilters.includes(ANTITAGS_FILTER) && { antiTags: defaultFormValues.antiTags }),
                    }
                    applyFilters(resettedFilters)
                    resetForm({ values: resettedFilters })
                    close()
                }}
                variant="text"
            >
                {isMobile ? t(I18N_NAMESPACE, "forms.techniquesFiltersForm.buttons.delete%small")
                    : t(I18N_NAMESPACE, "forms.techniquesFiltersForm.buttons.delete")}
            </Styled.Button>
            <Styled.Wrapper>
                <Styled.Button
                    id="cancel-filters-button"
                    onClick={() => {
                        resetForm({ values: { ...initialValues } })
                        close()
                    }}
                    variant="tertiary"
                >
                    {t(I18N_NAMESPACE, "forms.techniquesFiltersForm.buttons.cancel")}
                </Styled.Button>
                <Styled.SubmitButton
                    id="submit-filters-button"
                    type="submit"
                >
                    {t(I18N_NAMESPACE, "forms.techniquesFiltersForm.buttons.confirm")}
                </Styled.SubmitButton>
            </Styled.Wrapper>
        </>
    }, [
        enabledFilters,
        applyFilters,
        close,
        isMobile,
        filters,
        initialValues,
        t,
    ])

    const getForm = useCallback((resetForm, setFieldValue, values) => {
        if (isMobile) {
            return <Styled.Form>
                <ScrollableLayout
                    className={className}
                    content={getFormFields(setFieldValue,values)}
                    buttons={getFormButtons(resetForm)}
                />
            </Styled.Form>
        } else {
            return <Form>
                <Styled.FormWrapper>
                    {getFormFields(setFieldValue,values)}
                    <Styled.ButtonsWrapper>
                        {getFormButtons(resetForm)}
                    </Styled.ButtonsWrapper>
                </Styled.FormWrapper >
            </Form >
        }
    }, [getFormButtons, getFormFields, className, isMobile])

    return <Formik
        className={className}
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={onSubmit}
    >
        {({ resetForm, setFieldValue, values }) => getForm(resetForm, setFieldValue, values)}
    </Formik >
}

TechniquesFiltersForm.propTypes = {
    enabledFilters: PropTypes.arrayOf(PropTypes.oneOf(FILTERS)).isRequired,
    className: PropTypes.string,
    close: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    years: PropTypes.arrayOf(PropTypes.number.isRequired),
}
